import { useQuery } from '@tanstack/react-query';
import { supabase } from '@core/supabase/client';
import { APPLICATION_STATUSES, type ApplicationStatus } from '@core/enums';

const FETCH_CAP = 24;
const STUCK_DAYS = 3;
const NEEDS_REVIEW_MATCH_MIN = 75;
const NEW_HIGH_MATCH_MIN = 80;

const TERMINAL_STATUSES: ApplicationStatus[] = [
  'placed',
  'rejected',
  'closed',
  'offer_rejected',
  'no_show',
];

const ACTIVE_STATUSES_FOR_STUCK = APPLICATION_STATUSES.filter((s) => !TERMINAL_STATUSES.includes(s));

/**
 * Stand-in until a real assignee task inbox exists (e.g. ggj_b2b_recruiter_tasks).
 * TODO: replace with task rows where assignee = current user and status != done.
 */
const AWAITING_ME_STATUSES: ApplicationStatus[] = [
  'submitted',
  'viewed_by_client',
  'shortlisted',
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
];

function isoHoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 3600000).toISOString();
}

function localDayBoundsIso(): { startIso: string; endIso: string } {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const start = new Date(y, m, day, 0, 0, 0, 0);
  const end = new Date(y, m, day + 1, 0, 0, 0, 0);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

export type NeedsReviewRow =
  | {
      kind: 'stuck_application';
      id: string;
      applicationId: string;
      title: string;
      subtitle: string;
    }
  | {
      kind: 'fresh_match';
      id: string;
      candidateId: string;
      jobId: string;
      title: string;
      subtitle: string;
      score: number;
    };

export type InterviewTodayRow = {
  id: string;
  applicationId: string;
  title: string;
  subtitle: string;
  scheduledAt: string;
  round: number;
};

export type AwaitingMeRow = {
  applicationId: string;
  title: string;
  subtitle: string;
  status: ApplicationStatus;
};

export type NewHighMatchRow = {
  id: string;
  candidateId: string;
  jobId: string;
  clientId: string;
  title: string;
  subtitle: string;
  score: number;
};

type StuckAppDb = {
  id: string;
  status: ApplicationStatus;
  status_updated_at: string;
  candidate: { full_name: string } | null;
  job: { title: string } | null;
};

type MatchDb = {
  id: string;
  candidate_id: string;
  job_id: string;
  composite_score: number;
  created_at: string;
  candidate: { full_name: string } | null;
  job: { title: string; client_id: string } | null;
};

async function fetchNeedsReview(orgId: string): Promise<{
  rows: NeedsReviewRow[];
  totalListed: number;
}> {
  const stuckCutoff = new Date(Date.now() - STUCK_DAYS * 86400000).toISOString();
  const since24 = isoHoursAgo(24);

  const [stuckRes, matchRes] = await Promise.all([
    supabase
      .from('ggj_b2b_applications')
      .select(
        `id, status, status_updated_at, candidate:ggj_b2b_candidates(full_name), job:ggj_b2b_jobs(title)`
      )
      .eq('org_id', orgId)
      .in('status', ACTIVE_STATUSES_FOR_STUCK)
      .lt('status_updated_at', stuckCutoff)
      .order('status_updated_at', { ascending: true })
      .limit(FETCH_CAP),
    supabase
      .from('ggj_b2b_match_results')
      .select(
        `id, candidate_id, job_id, composite_score, created_at, candidate:ggj_b2b_candidates(full_name), job:ggj_b2b_jobs(title, client_id)`
      )
      .eq('org_id', orgId)
      .gte('composite_score', NEEDS_REVIEW_MATCH_MIN)
      .gte('created_at', since24)
      .order('created_at', { ascending: false })
      .limit(FETCH_CAP),
  ]);

  if (stuckRes.error) throw stuckRes.error;
  if (matchRes.error) throw matchRes.error;

  const stuckRows = (stuckRes.data ?? []) as StuckAppDb[];
  const matchRows = (matchRes.data ?? []) as MatchDb[];

  const stuckItems: NeedsReviewRow[] = stuckRows.map((r) => ({
    kind: 'stuck_application',
    id: `app-${r.id}`,
    applicationId: r.id,
    title: r.candidate?.full_name?.trim() || 'Candidate',
    subtitle: `${r.job?.title ?? 'Role'} · ${r.status.replace(/_/g, ' ')}`,
  }));

  const matchItems: NeedsReviewRow[] = matchRows.map((r) => ({
    kind: 'fresh_match',
    id: `match-${r.id}`,
    candidateId: r.candidate_id,
    jobId: r.job_id,
    title: r.candidate?.full_name?.trim() || 'Candidate',
    subtitle: `${r.job?.title ?? 'Role'} · match ${Math.round(r.composite_score)}`,
    score: r.composite_score,
  }));

  const ordered = [...matchItems, ...stuckItems].slice(0, 5);
  const totalListed = matchItems.length + stuckItems.length;

  return { rows: ordered, totalListed };
}

async function fetchInterviewsToday(orgId: string): Promise<{
  rows: InterviewTodayRow[];
  totalListed: number;
}> {
  const { startIso, endIso } = localDayBoundsIso();

  const { data, error } = await supabase
    .from('ggj_b2b_interview_events')
    .select(
      `id, application_id, scheduled_at, round, status,
       application:ggj_b2b_applications(
         candidate:ggj_b2b_candidates(full_name),
         job:ggj_b2b_jobs(title)
       )`
    )
    .eq('org_id', orgId)
    .not('scheduled_at', 'is', null)
    .gte('scheduled_at', startIso)
    .lt('scheduled_at', endIso)
    .neq('status', 'cancelled')
    .order('scheduled_at', { ascending: true })
    .limit(FETCH_CAP);

  if (error) throw error;

  type AppEmbed = {
    candidate: { full_name: string } | { full_name: string }[] | null;
    job: { title: string } | { title: string }[] | null;
  } | null;

  type Row = {
    id: string;
    application_id: string;
    scheduled_at: string;
    round: number;
    application: AppEmbed | AppEmbed[];
  };

  const asOne = <T,>(x: T | T[] | null | undefined): T | null => {
    if (x == null) return null;
    return Array.isArray(x) ? (x[0] ?? null) : x;
  };

  const raw = (data ?? []) as Row[];
  const rows: InterviewTodayRow[] = raw.map((r) => {
    const app = asOne(r.application);
    const cand = asOne(app?.candidate);
    const job = asOne(app?.job);
    return {
      id: r.id,
      applicationId: r.application_id,
      title: cand?.full_name?.trim() || 'Candidate',
      subtitle: job?.title ?? 'Interview',
      scheduledAt: r.scheduled_at,
      round: r.round,
    };
  });

  return { rows: rows.slice(0, 5), totalListed: rows.length };
}

async function fetchAwaitingMe(orgId: string, orgUserId: string | null): Promise<{
  rows: AwaitingMeRow[];
  totalListed: number;
}> {
  if (!orgUserId) {
    return { rows: [], totalListed: 0 };
  }

  const { data, error } = await supabase
    .from('ggj_b2b_applications')
    .select(
      `id, status, candidate:ggj_b2b_candidates(full_name), job:ggj_b2b_jobs(title)`
    )
    .eq('org_id', orgId)
    .eq('assigned_to', orgUserId)
    .in('status', AWAITING_ME_STATUSES)
    .order('status_updated_at', { ascending: true })
    .limit(FETCH_CAP);

  if (error) throw error;

  type Row = {
    id: string;
    status: ApplicationStatus;
    candidate: { full_name: string } | null;
    job: { title: string } | null;
  };

  const raw = (data ?? []) as Row[];
  const rows: AwaitingMeRow[] = raw.map((r) => ({
    applicationId: r.id,
    title: r.candidate?.full_name?.trim() || 'Candidate',
    subtitle: `${r.job?.title ?? 'Role'} · ${r.status.replace(/_/g, ' ')}`,
    status: r.status,
  }));

  return { rows: rows.slice(0, 5), totalListed: rows.length };
}

async function fetchNewHighMatches(orgId: string): Promise<{
  rows: NewHighMatchRow[];
  totalListed: number;
}> {
  const since24 = isoHoursAgo(24);

  const { data, error } = await supabase
    .from('ggj_b2b_match_results')
    .select(
      `id, candidate_id, job_id, composite_score, candidate:ggj_b2b_candidates(full_name), job:ggj_b2b_jobs(title, client_id)`
    )
    .eq('org_id', orgId)
    .gte('composite_score', NEW_HIGH_MATCH_MIN)
    .gte('created_at', since24)
    .order('composite_score', { ascending: false })
    .limit(FETCH_CAP);

  if (error) throw error;

  type Row = {
    id: string;
    candidate_id: string;
    job_id: string;
    composite_score: number;
    candidate: { full_name: string } | null;
    job: { title: string; client_id: string } | null;
  };

  const raw = (data ?? []) as Row[];
  const rows: NewHighMatchRow[] = raw.map((r) => ({
    id: r.id,
    candidateId: r.candidate_id,
    jobId: r.job_id,
    clientId: r.job?.client_id ?? '',
    title: r.candidate?.full_name?.trim() || 'Candidate',
    subtitle: r.job?.title ?? 'Role',
    score: r.composite_score,
  }));

  return { rows: rows.slice(0, 5), totalListed: rows.length };
}

export function useHomeQueues(orgId: string | undefined, orgUserId: string | null | undefined) {
  const needsReview = useQuery({
    queryKey: ['homeQueues', 'needsReview', orgId],
    queryFn: () => fetchNeedsReview(orgId!),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });

  const interviewsToday = useQuery({
    queryKey: ['homeQueues', 'interviewsToday', orgId],
    queryFn: () => fetchInterviewsToday(orgId!),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });

  const awaitingMe = useQuery({
    queryKey: ['homeQueues', 'awaitingMe', orgId, orgUserId],
    queryFn: () => fetchAwaitingMe(orgId!, orgUserId ?? null),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });

  const newHighMatches = useQuery({
    queryKey: ['homeQueues', 'newHighMatches', orgId],
    queryFn: () => fetchNewHighMatches(orgId!),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });

  return { needsReview, interviewsToday, awaitingMe, newHighMatches };
}
